import { defaultsDeep, isNil, isPlainObject, mapValues } from 'lodash-es';
import { isMobile } from 'react-device-detect';

import { services } from 'services/base';
import {
  ActualPlatformConfigs,
  GetPlatformConfigService,
  OnPlatformChangeService,
  OnPlatformChangeServiceCallback,
  PlatformConfig,
  PlatformConfigs,
} from 'types';
import { getMainConfig, logger } from 'utils';

const defaultPlatformConfigs: ActualPlatformConfigs = {
  mobile: {
    name: 'mobile',
    viewportWidth: 720,
    viewportHeight: 450,
    hideAppHeader: true,
    hideSidebarNavigation: true,
    disabled: true,
  },
  tablet: {
    name: 'tablet',
    disabled: true,
  },
  desktop: {
    name: 'desktop',
  },
};

type PlatformState = {
  config: PlatformConfig;
};

type DeviceMatches = {
  isTabletView: boolean;
  isMobileWidth: boolean;
  isMobileHeight: boolean;
  portrait: boolean;
};

// Returns the default platform name, ordered from largest to smallest size.
const getDefaultPlatformName = (platformConfigs: ActualPlatformConfigs) => {
  if (!platformConfigs.desktop.disabled) {
    return 'desktop';
  }
  if (!platformConfigs.tablet.disabled) {
    return 'tablet';
  }
  if (!platformConfigs.mobile.disabled) {
    return 'mobile';
  }
  throw new Error('At least one platform must be enabled in platform configuration!');
};

const calculatePlatformName = (
  { isTabletView, isMobileWidth, isMobileHeight, portrait }: DeviceMatches,
  platformConfigs: ActualPlatformConfigs,
) => {
  // please note that `isMobile` returns `true` for mobile and tablet devices too
  const isMobileView = (isMobile && portrait) || isMobileWidth || isMobileHeight;

  if (!platformConfigs.mobile.disabled && isMobileView) {
    return 'mobile';
  }

  if (!platformConfigs.tablet.disabled && isTabletView) {
    return 'tablet';
  }

  return getDefaultPlatformName(platformConfigs);
};

const SERVICE_PREFIX = 'appFrame.platform';
const ACTION_PLATFORM_CHANGE = 'AppFrame:Platform:Change';

const initPlatformServices = () => {
  const { registerService, executeAction, registerAction, clearAction } = services;

  const state: PlatformState = {
    config: defaultPlatformConfigs.desktop,
  };

  const updatePlatformState = () => {
    const platformConfigsFromConfig: PlatformConfigs = getMainConfig('platforms') || defaultPlatformConfigs;
    const platformConfigs: ActualPlatformConfigs = mapValues(defaultPlatformConfigs, (defaultPlatformConfig, name) => {
      const platformConfig = platformConfigsFromConfig[name];

      if (typeof platformConfig === 'boolean') {
        return { ...defaultPlatformConfig, disabled: !platformConfig };
      }

      if (isPlainObject(platformConfig)) {
        return defaultsDeep({}, platformConfig, defaultPlatformConfig);
      }

      if (!isNil(platformConfig)) {
        logger.warn(`Platform configuration '${name}' is invalid - default config is used!`);
      }

      return defaultPlatformConfig;
    });

    const { mobile: mobilePlatformConfig, tablet: tabletPlatformConfig } = platformConfigs;
    const tabletQuery = window.matchMedia(`(max-width: ${tabletPlatformConfig?.viewportWidth || 0}px)`);
    const mobileWidthQuery = window.matchMedia(`(max-width: ${mobilePlatformConfig?.viewportWidth || 0}px)`);
    const mobileHeightQuery = window.matchMedia(`(max-height: ${mobilePlatformConfig?.viewportHeight || 0}px)`);
    const portraitQuery = window.matchMedia('(orientation: portrait)');
    const handleQueryChange = () => {
      tabletQuery.onchange = null;
      mobileWidthQuery.onchange = null;
      mobileHeightQuery.onchange = null;
      portraitQuery.onchange = null;

      const disablePlatformChange = getMainConfig('disablePlatformChange') || false;
      if (disablePlatformChange) {
        return;
      }

      updatePlatformState();
    };
    tabletQuery.onchange = handleQueryChange;
    mobileWidthQuery.onchange = handleQueryChange;
    mobileHeightQuery.onchange = handleQueryChange;
    portraitQuery.onchange = handleQueryChange;
    const deviceMatches: DeviceMatches = {
      isTabletView: tabletQuery.matches,
      isMobileWidth: mobileWidthQuery.matches,
      isMobileHeight: mobileHeightQuery.matches,
      portrait: portraitQuery.matches,
    };

    const platformName = calculatePlatformName(deviceMatches, platformConfigs);
    if (platformName !== state.config.name) {
      const platformConfig = platformConfigs[platformName];
      executeAction(ACTION_PLATFORM_CHANGE, platformConfig);
    }
  };

  registerAction(
    ACTION_PLATFORM_CHANGE,
    (config: PlatformConfig) => {
      state.config = config;
    },
    { order: 0 },
  );

  registerService<GetPlatformConfigService>(`${SERVICE_PREFIX}.getPlatformConfig`, () => state.config);

  registerService<OnPlatformChangeService>(
    `${SERVICE_PREFIX}.onPlatformChange`,
    (callback: OnPlatformChangeServiceCallback) => {
      registerAction(ACTION_PLATFORM_CHANGE, callback, { order: 1 });
      return () => clearAction(ACTION_PLATFORM_CHANGE, callback);
    },
  );

  updatePlatformState();
};

export default initPlatformServices;
